import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  onAuthStateChanged, 
  signInWithPopup, 
  GoogleAuthProvider, 
  signOut,
  User as FirebaseUser
} from 'firebase/auth';
import { auth } from '../lib/firebase';

interface User {
  id: string;
  email: string;
  name: string;
  picture: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user && !!token;

  // Convert Firebase user to our User interface
  const convertFirebaseUser = (firebaseUser: FirebaseUser): User => ({
    id: firebaseUser.uid,
    email: firebaseUser.email || '',
    name: firebaseUser.displayName || 'User',
    picture: firebaseUser.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(firebaseUser.displayName || 'User')}&background=4285f4&color=fff`
  });

  // Listen to Firebase auth state changes
  useEffect(() => {
    // If Firebase is not available, clear any old auth and require fresh login
    if (!auth) {
      localStorage.removeItem('memorious_auth');
      localStorage.removeItem('memorious_token');
      setUser(null);
      setToken(null);
      setIsLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setIsLoading(true);
      
      if (firebaseUser) {
        try {
          // Get the ID token from Firebase
          const idToken = await firebaseUser.getIdToken();
          const userData = convertFirebaseUser(firebaseUser);
          
          // Store in state
          setUser(userData);
          setToken(idToken);
          
          // Store in localStorage for persistence
          localStorage.setItem('memorious_auth', JSON.stringify(userData));
          localStorage.setItem('memorious_token', idToken);
          
        } catch (error) {
          console.error('Failed to get user token:', error);
          setUser(null);
          setToken(null);
        }
      } else {
        // User is signed out
        setUser(null);
        setToken(null);
        localStorage.removeItem('memorious_auth');
        localStorage.removeItem('memorious_token');
      }
      
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const loginWithGoogle = async () => {
    if (!auth) {
      throw new Error('Firebase not configured. Please set up Firebase environment variables.');
    }
    
    try {
      const provider = new GoogleAuthProvider();
      // Add scopes if needed for Google services integration
      provider.addScope('email');
      provider.addScope('profile');
      
      const result = await signInWithPopup(auth, provider);
      // The onAuthStateChanged listener will handle setting the user state
      console.log('Google sign-in successful:', result.user.email);
    } catch (error) {
      console.error('Google sign-in failed:', error);
      throw new Error('Failed to sign in with Google');
    }
  };


  const logout = async () => {
    try {
      if (auth) {
        await signOut(auth);
        // The onAuthStateChanged listener will handle clearing the user state
      } else {
        // Manual cleanup when Firebase not available
        setUser(null);
        setToken(null);
        localStorage.removeItem('memorious_auth');
        localStorage.removeItem('memorious_token');
      }
    } catch (error) {
      console.error('Logout failed:', error);
      // Force clear state even if Firebase signOut fails
      setUser(null);
      setToken(null);
      localStorage.removeItem('memorious_auth');
      localStorage.removeItem('memorious_token');
      throw new Error('Failed to sign out');
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated,
    isLoading,
    loginWithGoogle,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}